// Stats readout (ui-flow §6.5): "{fps} fps · {ping} ms", updated every 500 ms. In M1 it is shown
// with the `?stats` URL flag; in M2 by the "Show FPS & ping" setting.
//
// Implementer: client UI. Consumer: app.ts (feeds frame times and socket.rttMs).
//
// FPS follows the roadmap's definition: 1000 / (mean requestAnimationFrame interval) over the
// window, i.e. frames counted in the window divided by its length. `FpsMeter` is DOM-free.

import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';

export interface StatsHud {
  readonly el: HTMLElement;
  /** Call every display frame; the text refreshes at most every 500 ms. */
  frame(nowMs: number, rttMs: number): void;
  /** Average FPS over the last 500 ms window (also used by Auto graphics). */
  readonly fps: number;
  setVisible(visible: boolean): void;
}

export const STATS_WINDOW_MS = 500;

/** Counts display frames and turns them into an average FPS once per window. */
export class FpsMeter {
  readonly windowMs: number;
  /** FPS of the last completed window (0 before the first). */
  fps = 0;
  private start = Number.NaN;
  private intervals = 0;
  private last = Number.NaN;

  constructor(windowMs: number = STATS_WINDOW_MS) {
    this.windowMs = windowMs;
  }

  /** Records a frame at `nowMs`; true when a window just completed (`fps` updated). */
  frame(nowMs: number): boolean {
    if (!Number.isFinite(this.start)) {
      this.start = nowMs;
      this.last = nowMs;
      this.intervals = 0;
      return false;
    }
    if (nowMs < this.last) {
      // The clock went backwards (should not happen with performance.now): start over.
      this.start = nowMs;
      this.last = nowMs;
      this.intervals = 0;
      return false;
    }
    this.intervals++;
    this.last = nowMs;
    const elapsed = nowMs - this.start;
    if (elapsed < this.windowMs) return false;
    this.fps = (this.intervals * 1000) / elapsed;
    this.start = nowMs;
    this.intervals = 0;
    return true;
  }

  reset(): void {
    this.start = Number.NaN;
    this.last = Number.NaN;
    this.intervals = 0;
    this.fps = 0;
  }
}

/** "{fps} fps · {ping} ms"; the ping shows "–" until the first PONG. */
export function statsText(fps: number, rttMs: number): string {
  const ping = rttMs > 0 && Number.isFinite(rttMs) ? Math.round(rttMs) : '–';
  return t('hud.stats', { fps: Math.round(fps), ping });
}

export function createStatsReadout(): StatsHud {
  const meter = new FpsMeter();
  const el = h('div', { class: 'hud-stats', 'aria-live': 'off', hidden: true });
  let text = '';
  return {
    el,
    frame(nowMs: number, rttMs: number): void {
      if (!meter.frame(nowMs)) return;
      const next = statsText(meter.fps, rttMs);
      if (next !== text) {
        text = next;
        el.textContent = next;
      }
    },
    get fps(): number {
      return meter.fps;
    },
    setVisible(visible: boolean): void {
      if (visible && el.hidden) meter.reset();
      show(el, visible);
    },
  };
}
